import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { ApiConstants } from '../../../shared/constants/api-constants';
import { UserTrainingPlanDTO } from '../models/user-training-plan';
import { TrainingOccurrenceDTO } from '../models/training-occurrence';
import { FilterAndSortDetails } from '../../../shared/models/filter-sort-details';
import { TrainingPlanExportResponse } from '../models/trainingplan-export-response';
import { MySpaceConstants } from '../../my-space-constants';
import { CommonService } from '../../../shared/services/common.service';
import { UserTrainingPlanReviewHistoryDetail } from '../models/user-training-plan-review-history-detail';
import { TrainingQuizAnswerDto } from '../models/trainingQuizAnswerDto';
import { TrainingPlanDetailsDto } from '../models/trainingPlanDetailsDto';
import { TrainingOccurrenceSessionDetailsDto } from '../models/training-occurrence-session-details';
import { TrainingPlanSearchRequest } from '../models/training-plan-search-request';
import { TrainingPlanQueryResult } from '../models/training-plan-query-result';
import { TrainingPlanLookup } from '../models/training-plan-lookup';

@Injectable({
  providedIn: 'root'
})
export class UserTrainingPlanService {

  // Cache for lookup data (filters/sorts) - shared across all component instances
  private trainingPlanLookupCache$: Observable<TrainingPlanLookup> | null = null;
  
  constructor(private httpClient: HttpClient,
    private commonService:CommonService) { }

  
  getUserTrainingPlan(): Observable<TrainingPlanDetailsDto> {
    return this.httpClient.get<TrainingPlanDetailsDto>(ApiConstants.GetUserTrainingPlan_Api);
  }

  private trainingItemsSubject = new BehaviorSubject<UserTrainingPlanDTO[]>([]);
  public trainingItems$ = this.trainingItemsSubject.asObservable();

  private progressKPISubject = new BehaviorSubject<any | null>(null);
  public progressKPI$ = this.progressKPISubject.asObservable();

  updateTrainingItems(trainingItems: UserTrainingPlanDTO[]): void {
    this.trainingItemsSubject.next(trainingItems);
  }

  updateProgressKPI(progressKPI: any): void {
    this.progressKPISubject.next(progressKPI);
  }
  getTrainingRequirementHistoryOnId(trainingItemNumber: string, selectedPuid?: string | null): Observable<TrainingOccurrenceDTO[]> {
    let url = `${ApiConstants.GetTrainingRequirementHistory_Api}?trainingItemNumber=${trainingItemNumber}`;
    if (selectedPuid) {
      url += `&puid=${selectedPuid}`;
    }
    return this.httpClient.get<TrainingOccurrenceDTO[]>(url);
  }
  
  /**
   * Export training plan using same search criteria as search endpoint
   * @param searchRequest The search request with filters, sort, and criteria
   * @returns Observable of TrainingPlanExportResponse
   */
  exportTrainingPlan(searchRequest: TrainingPlanSearchRequest): Observable<TrainingPlanExportResponse> {
    return this.httpClient.post<TrainingPlanExportResponse>(
      ApiConstants.TrainingPlan_Export_Api, 
      searchRequest
    );
  }

  getTrainingPlanReviewHistoryDetailsPerUser(userPuid: string) : Observable<UserTrainingPlanReviewHistoryDetail[]>{
    return this.httpClient.get<UserTrainingPlanReviewHistoryDetail[]>(ApiConstants.TrainingPlanReview_HistoryDetail.replace("{userPuid}", userPuid));
  }

  getTrainingQuizAnswerAudit(trainingQuizAnswerId: string):Observable<TrainingQuizAnswerDto[]>{
    return this.httpClient.get<TrainingQuizAnswerDto[]>(ApiConstants.TrainingQuizAnswer_GetAuditDetails.replace("{trainingQuizAnswerId}",trainingQuizAnswerId))
  }

  getTrainingOccurrenceSessionDetails(sessionIds: string[]): Observable<TrainingOccurrenceSessionDetailsDto[]> {
    return this.httpClient.post<TrainingOccurrenceSessionDetailsDto[]>(ApiConstants.GetTrainingOccurrenceSessionDetails_Api, sessionIds);
  }

  /**
   * Search training plan with pagination, filtering, and sorting
   * @param searchRequest The search request with pagination, filter, and sort criteria
   * @returns Observable of TrainingPlanQueryResult with paginated data
   */
  searchTrainingPlan(searchRequest: TrainingPlanSearchRequest): Observable<TrainingPlanQueryResult> {
    return this.httpClient.post<TrainingPlanQueryResult>(ApiConstants.TrainingPlan_Search_Api, searchRequest);
  }

  /**
   * Get lookup data for training plan filters and sorts
   * Cached to avoid multiple requests - lookup data is the same for all users
   * @returns Observable of TrainingPlanLookup with all filter/sort options
   */
  getTrainingPlanLookup(): Observable<TrainingPlanLookup> {
    if (!this.trainingPlanLookupCache$) {
      // First request - fetch and cache with shareReplay
      this.trainingPlanLookupCache$ = this.httpClient.get<TrainingPlanLookup>(ApiConstants.TrainingPlan_Lookup_Api).pipe(
        shareReplay(1) // Cache the result and replay to new subscribers
      );
    }
    return this.trainingPlanLookupCache$;
  }

}
